import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { Subject, takeUntil } from 'rxjs';

import {
  CheckboxMultiDropdownComponent,
  CheckboxMultiDropdownFooterAction,
  CheckboxMultiDropdownOption,
} from '../../../shared/components/checkbox-multi-dropdown/checkbox-multi-dropdown.component';
import { ModalFooterActionsComponent } from '../../../shared/components/modal-footer-actions/modal-footer-actions.component';
import { Checkbox } from '../../../shared/components/checkbox/checkbox.component';
import { CustomDatePickerComponent } from '../../../components/custom-date-picker/custom-date-picker.component';
import { CurrencyActionInputComponent } from '../../../shared/components/currency-action-input/currency-action-input.component';

import { LocationService } from '../../../services/location.service';
import { PackageService } from '../../../services/package.service';
import { ToastService } from '../../../services/toast.service';
import { LoggerService } from '../../../services/logger.service';
import { DiscountService } from '../../services/discount.service';
import {
  Discount,
  DiscountCustomSchedule,
  DiscountPayload,
  DiscountType,
  DiscountWashType,
  RenewalCycle,
  WeekDay,
} from '../../models/discount.models';
import {
  DISCOUNT_NAME_MAX_LENGTH,
  DISCOUNT_TYPE_OPTIONS,
  ELIGIBILITY_OPTIONS,
  MAX_CUSTOM_SCHEDULE_RENEWALS,
  MAX_RECURRING_RENEWAL_MONTHS,
  MIN_DISCOUNT_INTERVAL_MINUTES,
  RENEWAL_CYCLE_CARDS,
  WASH_TYPE_OPTIONS,
  WEEK_DAYS,
} from '../../constants/discount.constants';
import { normalizeTimeForForm } from '../../utils/discount-display.util';
import { DropdownToggleHelper } from '../../../shared/helpers/dropdown-toggle.helper';
import { getUserTimezone, parseWallClockDateTimeInTimeZone } from '../../../shared/utils/date.util';
import {
  addMinutesToDate,
  isDateAtLeastMinutesAfter,
  isTimeAtLeastMinutesAfter,
  parseTimeToMinutes,
} from '../../../shared/utils/time.util';

/**
 * Create / Edit Discount drawer (CWS-1176).
 *
 * Field rules enforced here mirror the API so the user sees them before submitting:
 *  - percentage values are capped at 100
 *  - a date range is required unless "Always Active" is on (end ≥ start + 30 min)
 *  - the from/to time restriction must be set together (to ≥ from + 30 min)
 *  - Location, Wash Type, and Wash Package are required multi-selects (no empty default)
 *  - Wash Type "All wash" is a UI Select All that emits every wash-type id
 *  - Day of Week has no default / "Every Day" selection; edit uses API values only
 *  - Wash Package options are loaded for the selected Wash Type(s) only
 *  - the Advance Renewal Cycle panel only exists when Wash Type is exactly
 *    Monthly Membership, and a custom schedule needs at least one renewal row
 */
@Component({
  selector: 'app-create-discount-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatRadioModule,
    CheckboxMultiDropdownComponent,
    ModalFooterActionsComponent,
    Checkbox,
    CustomDatePickerComponent,
    CurrencyActionInputComponent,
  ],
  templateUrl: './create-discount-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./create-discount-modal.component.scss'],
})
export class CreateDiscountModalComponent implements OnChanges, OnDestroy {
  /** When set, the drawer edits this discount; otherwise it creates a new one. */
  @Input() discount: Discount | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<Discount | null>();

  private readonly destroy$ = new Subject<void>();
  /** Tears down per-form subscriptions when the form group is rebuilt. */
  private readonly formReset$ = new Subject<void>();
  private readonly fb = inject(FormBuilder);
  private readonly discountService = inject(DiscountService);
  private readonly locationService = inject(LocationService);
  private readonly packageService = inject(PackageService);
  private readonly toastService = inject(ToastService);
  private readonly logger = inject(LoggerService);

  readonly discountTypeOptions = DISCOUNT_TYPE_OPTIONS;
  readonly eligibilityOptions = ELIGIBILITY_OPTIONS;
  readonly washTypeOptions = WASH_TYPE_OPTIONS;
  readonly weekDays = WEEK_DAYS;
  readonly renewalCards = RENEWAL_CYCLE_CARDS;
  readonly maxRenewals = MAX_CUSTOM_SCHEDULE_RENEWALS;
  readonly maxRecurringMonths = MAX_RECURRING_RENEWAL_MONTHS;
  readonly nameMaxLength = DISCOUNT_NAME_MAX_LENGTH;
  /** Figma Create Discount checkbox box size (px). */
  readonly checkboxSize = 22;
  /** Shared Clear footer action for Location / Day / Wash Type / Wash Package. */
  readonly clearFooterAction: CheckboxMultiDropdownFooterAction = { label: 'Clear' };

  locationOptions: CheckboxMultiDropdownOption[] = [];
  packageOptions: CheckboxMultiDropdownOption[] = [];

  submitting = false;
  submitted = false;
  loadingDiscount = false;
  /** True while Wash Packages are loading for the selected Wash Type(s). */
  loadingPackages = false;

  /** Ignores stale package responses when Wash Type changes quickly. */
  private packageLoadSeq = 0;

  form: FormGroup = this.buildForm();

  ngOnChanges(): void {
    this.refreshScheduleShopClock();
    this.formReset$.next();
    this.form = this.buildForm();
    this.applyScheduleEnabledState();
    this.submitted = false;
    this.packageOptions = [];
    this.loadingPackages = false;
    this.packageLoadSeq += 1;
    this.loadLocations();

    if (!this.discount) {
      return;
    }

    // List rows carry `locations` (id + name) but not location_ids / package_ids /
    // custom_schedules — only GET /discounts/:id returns those, so fetch before
    // prefilling or the form would silently drop the existing selections.
    if (this.discount.location_ids === undefined) {
      this.loadFullDiscount(this.discount.id);
      return;
    }

    this.patchFromDiscount(this.discount);
  }

  private loadFullDiscount(id: string): void {
    this.loadingDiscount = true;
    this.discountService
      .getDiscountById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.loadingDiscount = false;
          if (response?.data) {
            this.discount = response.data;
            this.patchFromDiscount(response.data);
          }
        },
        error: error => {
          this.logger.error('Failed to load discount for editing', error);
          this.loadingDiscount = false;
          this.toastService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Could not load this discount. Please close and try again.',
          });
        },
      });
  }

  ngOnDestroy(): void {
    DropdownToggleHelper.closeAll();
    this.formReset$.next();
    this.formReset$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): FormGroup {
    const form = this.fb.group(
      {
        name: ['', [Validators.required, Validators.maxLength(DISCOUNT_NAME_MAX_LENGTH)]],
        is_active: [true],
        location_ids: [[] as string[], [Validators.required, Validators.minLength(1)]],
        discount_type: ['flat' as DiscountType, Validators.required],
        discount_value: [null as number | null, [Validators.required, Validators.min(0.01)]],
        eligibility: ['all_customer'],
        always_active: [true],
        start_date: [null as Date | null],
        end_date: [null as Date | null],
        restrict_days_of_week: [[] as WeekDay[]],
        restrict_from_time: [''],
        restrict_to_time: [''],
        wash_types: [[] as DiscountWashType[], [Validators.required, Validators.minLength(1)]],
        package_ids: [[] as string[], [Validators.required, Validators.minLength(1)]],
        renewal_cycle: ['standard' as RenewalCycle],
        recurring_renewal_months: [null as number | null],
        custom_schedules: this.fb.array([] as FormGroup[]),
        add_minimum_purchase: [false],
        minimum_purchase_amount: [null as number | null],
      },
      { validators: this.discountFormValidator() }
    );
    this.syncConditionalValidators(form);
    this.wireFormSubscriptions(form);
    return form;
  }

  private wireFormSubscriptions(form: FormGroup): void {
    form
      .get('discount_type')
      ?.valueChanges.pipe(takeUntil(this.formReset$), takeUntil(this.destroy$))
      .subscribe(() => this.syncConditionalValidators());
  }

  get customSchedules(): FormArray {
    return this.form.get('custom_schedules') as FormArray;
  }

  private scheduleGroup(schedule?: DiscountCustomSchedule): FormGroup {
    const group = this.fb.group({
      renewal_number: [schedule?.renewal_number ?? this.customSchedules.length + 1],
      discount_type: [schedule?.discount_type ?? 'percentage', Validators.required],
      discount_value: [
        schedule?.discount_value ?? null,
        [Validators.required, Validators.min(0.01), this.scheduleDiscountValueValidator()],
      ],
    });

    group
      .get('discount_type')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => group.get('discount_value')?.updateValueAndValidity({ emitEvent: false }));

    return group;
  }

  // ── Options ────────────────────────────────────────────────────────────────

  private loadLocations(): void {
    this.locationOptions = this.locationService
      .getLocationOptions()
      .map(option => ({ id: option.value, name: option.label }));

    // Normally already cached by the Discount page; fetch once if the drawer is first.
    if (this.locationOptions.length === 0) {
      this.locationService
        .getLocations()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.locationOptions = this.locationService
            .getLocationOptions()
            .map(option => ({ id: option.value, name: option.label }));
        });
    }
  }

  /**
   * Loads Wash Packages for the given Wash Type(s). Optionally applies
   * `packageIdsToApply` after options arrive (edit-mode API prefill).
   */
  private loadPackagesForWashTypes(
    washTypes: DiscountWashType[],
    options?: { packageIdsToApply?: string[] }
  ): void {
    // Packages are scoped to the chosen locations, so an empty Location selection has no
    // meaningful answer — offering every package would let the user pick one the location
    // does not sell.
    if (washTypes.length === 0 || this.selectedLocationIds.length === 0) {
      this.packageLoadSeq += 1;
      this.packageOptions = [];
      this.loadingPackages = false;
      return;
    }

    const seq = ++this.packageLoadSeq;
    this.loadingPackages = true;
    this.packageOptions = [];

    this.packageService
      .getPackagesByWashTypes(washTypes.join(','), {
        locationIds: this.selectedLocationIds,
        activeOnly: true,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: packages => {
          if (seq !== this.packageLoadSeq) {
            return;
          }
          this.packageOptions = (Array.isArray(packages) ? packages : [])
            .filter(pkg => Boolean(pkg?.id))
            .map(pkg => ({
              id: String(pkg.id),
              name: String(pkg.package_name || 'Package'),
            }));
          this.loadingPackages = false;

          const pendingIds = options?.packageIdsToApply;
          if (!pendingIds) {
            return;
          }
          const available = new Set(this.packageOptions.map(option => option.id));
          this.form.get('package_ids')?.setValue(pendingIds.filter(id => available.has(id)));
        },
        error: error => {
          if (seq !== this.packageLoadSeq) {
            return;
          }
          this.loadingPackages = false;
          this.packageOptions = [];
          this.logger.error('Failed to load packages for discount form', error);
        },
      });
  }

  // ── Conditional UI ─────────────────────────────────────────────────────────

  get isEditing(): boolean {
    return Boolean(this.discount?.id);
  }

  get title(): string {
    return this.isEditing ? 'Edit Discount' : 'Create Discount';
  }

  get primaryLabel(): string {
    return this.isEditing ? 'Save Changes' : 'Create Discount';
  }

  get isPercentage(): boolean {
    return this.form.get('discount_type')?.value === 'percentage';
  }

  get alwaysActive(): boolean {
    return Boolean(this.form.get('always_active')?.value);
  }

  /** Renewal options only exist for a monthly-membership-only discount (API rule). */
  get isMonthlyMembershipOnly(): boolean {
    const washTypes = (this.form.get('wash_types')?.value || []) as DiscountWashType[];
    return washTypes.length === 1 && washTypes[0] === 'monthly_membership';
  }

  get renewalCycle(): RenewalCycle {
    return (this.form.get('renewal_cycle')?.value || 'standard') as RenewalCycle;
  }

  /** The month count only exists for the Recurring card. */
  get showRecurringMonths(): boolean {
    return this.isMonthlyMembershipOnly && this.renewalCycle === 'advance_recurring';
  }

  get showCustomScheduleRows(): boolean {
    return this.isMonthlyMembershipOnly && this.renewalCycle === 'advance_custom';
  }

  /**
   * True while a custom schedule owns the signup and per-renewal numbers. Section A's
   * Discount Type/Value are faded then, because the First purchase discount row inside
   * the schedule edits those same two controls.
   */
  get isCustomScheduleMode(): boolean {
    return this.showCustomScheduleRows;
  }

  get canAddRenewal(): boolean {
    return this.customSchedules.length < this.maxRenewals;
  }

  /**
   * Shop clock for the Schedule pickers. An admin in IST scheduling a Los Angeles shop must
   * read and pick LA wall-clock times, so the pickers run on the same timezone the edit
   * prefill already uses. Kept as a stable object reference because the picker regenerates
   * its calendar whenever this input's timezone changes; an empty timezone makes the picker
   * fall back to browser-local, which is the old behaviour.
   */
  scheduleShopClock: { timezone: string } = { timezone: getUserTimezone() };

  /** Re-read on open, in case the admin switched shops since the last time. */
  private refreshScheduleShopClock(): void {
    const timezone = getUserTimezone();
    if (timezone !== this.scheduleShopClock.timezone) {
      this.scheduleShopClock = { timezone };
    }
  }

  /**
   * Create-mode Start floor: now, as an instant. The picker keeps the full 30-minute slot
   * list and disables slots strictly before this instant — so the next valid slot (or the
   * exact boundary slot when now lands on one) is the first enabled option. Slot instants
   * are built on `scheduleShopClock`, so the comparison is shop-time correct. Edit does not
   * use this as a value source; it prefills via `patchFromDiscount` and the picker keeps
   * the saved selection visible even when it is earlier than now.
   */
  get minStartDate(): Date {
    return new Date();
  }

  /**
   * End floor: Start + 30 minutes (Campaign `getEndDateMinDate()`).
   * Drives End picker minDate so earlier slots stay listed but disabled; the
   * first enabled End slot is Start + 30. When Start is unset, fall back to
   * browser-local now (same as Campaign).
   */
  get minEndDate(): Date {
    const start = this.toDate(this.form.get('start_date')?.value);
    if (!start) {
      return this.minStartDate;
    }
    return addMinutesToDate(start, MIN_DISCOUNT_INTERVAL_MINUTES);
  }

  /**
   * Earliest selectable To time (From + 30 minutes), used to disable earlier
   * slots while still showing the full time list.
   */
  get minToDate(): Date | null {
    const from = String(this.form.get('restrict_from_time')?.value || '');
    if (!from) {
      return null;
    }
    const minMinutes = parseTimeToMinutes(from) + MIN_DISCOUNT_INTERVAL_MINUTES;
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return addMinutesToDate(date, minMinutes);
  }

  get addMinimumPurchase(): boolean {
    return Boolean(this.form.get('add_minimum_purchase')?.value);
  }

  get selectedLocationIds(): string[] {
    return (this.form.get('location_ids')?.value || []) as string[];
  }

  /** Wash Package stays closed until Location and Wash Type are chosen (and while loading). */
  get isPackageDropdownDisabled(): boolean {
    const washTypes = (this.form.get('wash_types')?.value || []) as DiscountWashType[];
    return washTypes.length === 0 || this.selectedLocationIds.length === 0 || this.loadingPackages;
  }

  get packagePlaceholder(): string {
    const washTypes = (this.form.get('wash_types')?.value || []) as DiscountWashType[];
    if (this.selectedLocationIds.length === 0) {
      return 'Select Location first';
    }
    if (washTypes.length === 0) {
      return 'Select Wash Package';
    }
    if (this.loadingPackages) {
      return 'Loading packages...';
    }
    if (this.packageOptions.length === 0) {
      return 'No active packages for the selected locations';
    }
    return 'Select Wash Package';
  }

  /** Create/Save stays disabled until reactive form validation passes. */
  get isPrimaryDisabled(): boolean {
    return this.loadingDiscount || this.loadingPackages || this.form.invalid;
  }

  // ── Field handlers ─────────────────────────────────────────────────────────

  onDiscountTypeChange(type: DiscountType): void {
    this.form.get('discount_type')?.setValue(type);
    this.syncConditionalValidators();
  }

  onEligibilitySelect(value: string): void {
    this.form.get('eligibility')?.setValue(value);
  }

  /**
   * Wash Packages are location-scoped, so changing Location reloads them and drops any
   * selection the new locations no longer offer.
   */
  onLocationsChange(ids: string[]): void {
    this.form.get('location_ids')?.setValue(ids);
    this.form.get('location_ids')?.markAsTouched();

    const washTypes = (this.form.get('wash_types')?.value || []) as DiscountWashType[];
    const selectedPackageIds = (this.form.get('package_ids')?.value || []) as string[];
    this.loadPackagesForWashTypes(washTypes, { packageIdsToApply: selectedPackageIds });
  }

  onPackagesChange(ids: string[]): void {
    this.form.get('package_ids')?.setValue(ids);
    this.form.get('package_ids')?.markAsTouched();
  }

  onDaysChange(ids: string[]): void {
    this.form.get('restrict_days_of_week')?.setValue(ids as WeekDay[]);
  }

  /**
   * Changing Wash Type clears Wash Package (options may no longer apply) and
   * reloads packages for the new selection. Also resets renewal when the
   * discount is no longer monthly-membership-only.
   */
  onWashTypesChange(ids: string[]): void {
    this.form.get('wash_types')?.setValue(ids as DiscountWashType[]);
    this.form.get('wash_types')?.markAsTouched();
    this.form.get('package_ids')?.setValue([]);
    this.packageOptions = [];

    if (!this.isMonthlyMembershipOnly) {
      this.form.get('renewal_cycle')?.setValue('standard');
      this.customSchedules.clear();
    }

    this.syncConditionalValidators();
    this.loadPackagesForWashTypes(ids as DiscountWashType[]);
  }

  setAlwaysActive(active: boolean): void {
    if (this.alwaysActive === active) {
      return;
    }
    this.form.get('always_active')?.setValue(active);
    this.applyScheduleEnabledState();
    if (active) {
      this.form.get('start_date')?.setValue(null);
      this.form.get('end_date')?.setValue(null);
    }
    this.syncConditionalValidators();
  }

  onToggleAlwaysActive(): void {
    this.setAlwaysActive(!this.alwaysActive);
  }

  /**
   * Disable the date controls through the form (not a template `disabled` binding,
   * which Angular rejects on reactive controls).
   */
  private applyScheduleEnabledState(form: FormGroup = this.form): void {
    const alwaysActive = Boolean(form.get('always_active')?.value);
    const controls = [form.get('start_date'), form.get('end_date')];
    controls.forEach(control => {
      if (!control) {
        return;
      }
      if (alwaysActive) {
        control.clearValidators();
        control.disable({ emitEvent: false });
      } else {
        control.setValidators([Validators.required]);
        control.enable({ emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  setMinimumPurchase(enabled: boolean): void {
    if (this.addMinimumPurchase === enabled) {
      return;
    }
    this.form.get('add_minimum_purchase')?.setValue(enabled);
    if (!enabled) {
      this.form.get('minimum_purchase_amount')?.setValue(null);
    }
    this.syncConditionalValidators();
  }

  onToggleMinimumPurchase(): void {
    this.setMinimumPurchase(!this.addMinimumPurchase);
  }

  onFromTimeSelect(value: string): void {
    this.form.get('restrict_from_time')?.setValue(value ? value : null);
    this.clearToTimeIfInvalid();
    this.form.updateValueAndValidity();
  }

  onToTimeSelect(value: string): void {
    this.form.get('restrict_to_time')?.setValue(value ? value : null);
    this.form.updateValueAndValidity();
  }

  /** When Start Date changes, drop End Date if it is no longer Start + 30 minutes. */
  onStartDateChange(value: Date | null): void {
    this.clearEndDateIfInvalid(value);
    this.form.updateValueAndValidity();
  }

  /** Selecting the same card again clears it back to the standard cycle. */
  onSelectRenewalCycle(cycle: RenewalCycle): void {
    const next = this.renewalCycle === cycle ? 'standard' : cycle;
    this.form.get('renewal_cycle')?.setValue(next);

    if (next === 'advance_custom' && this.customSchedules.length === 0) {
      this.customSchedules.push(this.scheduleGroup());
    }
    if (next !== 'advance_custom') {
      this.customSchedules.clear();
    }
    // The API rejects a month count on any other cycle, so clear it when leaving Recurring.
    if (next !== 'advance_recurring') {
      this.form.get('recurring_renewal_months')?.setValue(null);
    }
    this.syncConditionalValidators();
  }

  /**
   * The month count drives a Stripe repeating coupon, capped at 12. `max` on a number
   * input only marks the field invalid rather than blocking the keystroke, so clamp the
   * typed value the way currency-action-input does.
   */
  onRecurringMonthsInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const control = this.form.get('recurring_renewal_months');
    if (!control) {
      return;
    }

    // A partially typed number input ("1e", "-") reads back as '', so strip to digits
    // and treat an empty field as cleared rather than as 0.
    const digits = input.value.replace(/[^\d]/g, '');
    if (digits === '') {
      input.value = '';
      control.setValue(null);
      return;
    }

    const clamped = Math.min(Math.max(Number(digits), 1), this.maxRecurringMonths);
    input.value = String(clamped);
    control.setValue(clamped);
  }

  /** Keeps the month field to whole numbers; `-`/`e`/`.` would blank a number input. */
  blockNonIntegerKeys(event: KeyboardEvent): void {
    if (['-', '+', 'e', 'E', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  }

  addRenewal(): void {
    if (!this.canAddRenewal) {
      return;
    }
    this.customSchedules.push(this.scheduleGroup());
    this.renumberSchedules();
    this.form.updateValueAndValidity();
  }

  removeRenewal(index: number): void {
    this.customSchedules.removeAt(index);
    this.renumberSchedules();
    this.form.updateValueAndValidity();
  }

  /**
   * A schedule covers renewals 1..n with no gaps, so the row's position is its cycle.
   * Product dropped the per-row cycle box, which is what let a schedule skip cycles.
   */
  private renumberSchedules(): void {
    this.customSchedules.controls.forEach((control, index) => {
      control.get('renewal_number')?.setValue(index + 1);
    });
  }

  // ── Validation ─────────────────────────────────────────────────────────────

  /** Keeps conditional validators aligned with the current discount configuration. */
  private syncConditionalValidators(form: FormGroup = this.form): void {
    const discountValue = form.get('discount_value');
    const discountValidators = [Validators.required, Validators.min(0.01)];
    if (form.get('discount_type')?.value === 'percentage') {
      discountValidators.push(Validators.max(100), this.wholeNumberValidator());
    } else {
      discountValidators.push(Validators.max(999999.99));
    }
    discountValue?.setValidators(discountValidators);
    discountValue?.updateValueAndValidity({ emitEvent: false });

    const minimumPurchase = form.get('minimum_purchase_amount');
    if (form.get('add_minimum_purchase')?.value) {
      minimumPurchase?.setValidators([Validators.required, Validators.min(0.01)]);
    } else {
      minimumPurchase?.clearValidators();
    }
    minimumPurchase?.updateValueAndValidity({ emitEvent: false });

    const recurringMonths = form.get('recurring_renewal_months');
    const washTypes = (form.get('wash_types')?.value || []) as DiscountWashType[];
    const isMonthlyOnly = washTypes.length === 1 && washTypes[0] === 'monthly_membership';
    const renewalCycle = (form.get('renewal_cycle')?.value || 'standard') as RenewalCycle;
    if (isMonthlyOnly && renewalCycle === 'advance_recurring') {
      recurringMonths?.setValidators([
        Validators.required,
        Validators.min(1),
        Validators.max(MAX_RECURRING_RENEWAL_MONTHS),
        this.wholeNumberValidator(),
      ]);
    } else {
      recurringMonths?.clearValidators();
    }
    recurringMonths?.updateValueAndValidity({ emitEvent: false });

    this.applyScheduleEnabledState(form);
    form.updateValueAndValidity({ emitEvent: false });
  }

  private wholeNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const amount = this.toFiniteNumber(control.value);
      if (amount === null) {
        return null;
      }
      return Number.isInteger(amount) ? null : { notInteger: true };
    };
  }

  private scheduleDiscountValueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control.parent;
      if (!group) {
        return null;
      }
      const amount = this.toFiniteNumber(control.value);
      if (amount === null) {
        return null;
      }
      if (group.get('discount_type')?.value === 'percentage' && amount > 100) {
        return { max: true };
      }
      return null;
    };
  }

  private discountFormValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const form = group as FormGroup;
      const value = form.getRawValue() as Record<string, unknown>;

      if (!value['always_active']) {
        const start = this.toDate(value['start_date']);
        const end = this.toDate(value['end_date']);
        if (!start || !end) {
          return { scheduleDates: true };
        }
        if (!isDateAtLeastMinutesAfter(end, start, MIN_DISCOUNT_INTERVAL_MINUTES)) {
          return { scheduleDateRange: true };
        }
      }

      const from = String(value['restrict_from_time'] || '');
      const to = String(value['restrict_to_time'] || '');
      if ((from && !to) || (!from && to)) {
        return { timeRangeIncomplete: true };
      }
      if (from && to && !isTimeAtLeastMinutesAfter(to, from, MIN_DISCOUNT_INTERVAL_MINUTES)) {
        return { timeRangeGap: true };
      }

      const washTypes = (value['wash_types'] || []) as DiscountWashType[];
      const isMonthlyOnly = washTypes.length === 1 && washTypes[0] === 'monthly_membership';
      const renewalCycle = (value['renewal_cycle'] || 'standard') as RenewalCycle;
      if (isMonthlyOnly && renewalCycle === 'advance_custom') {
        const schedules = form.get('custom_schedules') as FormArray;
        if (schedules.length === 0) {
          return { customScheduleEmpty: true };
        }
        if (schedules.length > MAX_CUSTOM_SCHEDULE_RENEWALS) {
          return { customScheduleTooMany: true };
        }
      }

      return null;
    };
  }

  private validationError(): string | null {
    const value = this.form.value as Record<string, unknown>;

    if (this.form.get('name')?.invalid) {
      return 'Enter a discount name.';
    }

    const locationIds = (value['location_ids'] || []) as string[];
    if (locationIds.length === 0) {
      return 'Select at least one location.';
    }

    // In custom-schedule mode this control is edited through the First purchase discount
    // row, so the message has to name that row rather than the faded Section A field.
    const amount = this.toFiniteNumber(value['discount_value']);
    if (amount === null || amount <= 0) {
      return this.isCustomScheduleMode
        ? 'Enter a first purchase discount greater than 0.'
        : 'Enter a discount value greater than 0.';
    }
    if (this.isPercentage && amount > 100) {
      return this.isCustomScheduleMode
        ? 'The first purchase discount cannot exceed 100%.'
        : 'A percentage discount cannot exceed 100%.';
    }
    if (this.isPercentage && !Number.isInteger(amount)) {
      return 'A percentage discount must be a whole number.';
    }
    if (!this.isPercentage && amount > 999999.99) {
      return 'A flat discount cannot exceed $999,999.99.';
    }

    if (!value['always_active']) {
      const start = this.toDate(value['start_date']);
      const end = this.toDate(value['end_date']);
      if (start && !end) {
        return 'End Date needs to be selected.';
      }
      if (!start && end) {
        return 'Start Date needs to be selected.';
      }
      if (!start || !end) {
        return 'Select a start and end date, or turn on Always Active.';
      }
      if (!isDateAtLeastMinutesAfter(end, start, MIN_DISCOUNT_INTERVAL_MINUTES)) {
        return `End date must be at least ${MIN_DISCOUNT_INTERVAL_MINUTES} minutes after the start date.`;
      }
    }

    const from = String(value['restrict_from_time'] || '');
    const to = String(value['restrict_to_time'] || '');
    if (from && !to) {
      return 'To needs to be selected.';
    }
    if (!from && to) {
      return 'From needs to be selected.';
    }
    if (from && to && !isTimeAtLeastMinutesAfter(to, from, MIN_DISCOUNT_INTERVAL_MINUTES)) {
      return `The to time must be at least ${MIN_DISCOUNT_INTERVAL_MINUTES} minutes after the from time.`;
    }

    const washTypes = (value['wash_types'] || []) as DiscountWashType[];
    if (washTypes.length === 0) {
      return 'Select at least one wash type.';
    }

    if (this.loadingPackages) {
      return 'Wait for wash packages to finish loading.';
    }

    const packageIds = (value['package_ids'] || []) as string[];
    if (packageIds.length === 0) {
      return 'Select at least one wash package.';
    }

    if (this.addMinimumPurchase) {
      // Number(null) and Number('') are 0, so an empty box would have passed here and
      // submitted a 0 minimum, making the checkbox silently do nothing.
      const minimum = this.toFiniteNumber(value['minimum_purchase_amount']);
      if (minimum === null || minimum <= 0) {
        return 'Enter a minimum purchase amount greater than 0.';
      }
    }

    if (this.showRecurringMonths) {
      // The API requires it for advance_recurring; without it nothing tells billing how
      // long to repeat the discount.
      const months = this.toFiniteNumber(value['recurring_renewal_months']);
      if (months === null || !Number.isInteger(months) || months < 1) {
        return 'Enter the number of months the recurring discount applies for.';
      }
      if (months > MAX_RECURRING_RENEWAL_MONTHS) {
        return `A recurring discount cannot run past ${MAX_RECURRING_RENEWAL_MONTHS} months.`;
      }
    }

    if (this.showCustomScheduleRows) {
      if (this.customSchedules.length === 0) {
        return 'Add at least one renewal to the custom schedule.';
      }
      // Rows are numbered by position, so the only way past the DB's 1-12 check
      // constraint is row count — reachable by reopening an over-long saved schedule.
      if (this.customSchedules.length > MAX_CUSTOM_SCHEDULE_RENEWALS) {
        return `A custom schedule cannot go past renewal ${MAX_CUSTOM_SCHEDULE_RENEWALS}.`;
      }
      if (this.customSchedules.invalid) {
        return 'Every renewal needs a discount value greater than 0.';
      }
      const overPercentage = this.customSchedules.controls.some(control => {
        const scheduleValue = Number(control.get('discount_value')?.value);
        return control.get('discount_type')?.value === 'percentage' && scheduleValue > 100;
      });
      if (overPercentage) {
        return 'A percentage renewal cannot exceed 100%.';
      }
    }

    return null;
  }

  /** Rejects blanks before coercion, since Number(null) and Number('') are both 0. */
  private toFiniteNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  }

  private toDate(value: unknown): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(String(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /**
   * Persist Start/End as UTC ISO instants (same as Campaign Schedule's
   * `Date#toISOString()` for schedule_*_date). Discount API stores one
   * TIMESTAMPTZ per bound — not separate date + HH:MM:SS fields — so we do not
   * also send `formatTimeFromDate` companions.
   */
  private toIsoString(value: unknown): string | null {
    return this.toDate(value)?.toISOString() ?? null;
  }

  /**
   * When Start moves within 30 minutes of End, clear End (Campaign Schedule
   * `onStartDateSelected` does the same).
   */
  private clearEndDateIfInvalid(startValue: unknown): void {
    const start = this.toDate(startValue);
    const end = this.toDate(this.form.get('end_date')?.value);
    if (!start || !end) {
      return;
    }
    if (!isDateAtLeastMinutesAfter(end, start, MIN_DISCOUNT_INTERVAL_MINUTES)) {
      this.form.get('end_date')?.setValue(null);
    }
  }

  private clearToTimeIfInvalid(): void {
    const from = String(this.form.get('restrict_from_time')?.value || '');
    const to = String(this.form.get('restrict_to_time')?.value || '');
    if (!from || !to) {
      return;
    }
    if (!isTimeAtLeastMinutesAfter(to, from, MIN_DISCOUNT_INTERVAL_MINUTES)) {
      this.form.get('restrict_to_time')?.setValue(null);
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  private buildPayload(): DiscountPayload {
    const value = this.form.getRawValue() as {
      name: string;
      is_active: boolean;
      discount_type: DiscountType;
      discount_value: number | null;
      eligibility: DiscountPayload['eligibility'];
      always_active: boolean;
      start_date: Date | null;
      end_date: Date | null;
      restrict_days_of_week: WeekDay[];
      restrict_from_time: string;
      restrict_to_time: string;
      wash_types: DiscountWashType[];
      location_ids: string[];
      package_ids: string[];
      minimum_purchase_amount: number | null;
      recurring_renewal_months: number | null;
    };
    const alwaysActive = Boolean(value.always_active);

    const payload: DiscountPayload = {
      name: String(value.name).trim(),
      is_active: Boolean(value.is_active),
      discount_type: value.discount_type,
      discount_value: Number(value.discount_value),
      eligibility: value.eligibility,
      always_active: alwaysActive,
      start_date: alwaysActive ? null : this.toIsoString(value.start_date),
      end_date: alwaysActive ? null : this.toIsoString(value.end_date),
      restrict_days_of_week: value.restrict_days_of_week || [],
      restrict_from_time: value.restrict_from_time || null,
      restrict_to_time: value.restrict_to_time || null,
      wash_types: value.wash_types || [],
      location_ids: value.location_ids || [],
      package_ids: value.package_ids || [],
      minimum_purchase_amount: this.addMinimumPurchase
        ? (this.toFiniteNumber(value.minimum_purchase_amount) ?? 0)
        : 0,
    };

    // renewal_cycle / custom_schedules are only accepted for monthly-membership-only.
    if (this.isMonthlyMembershipOnly) {
      payload.renewal_cycle = this.renewalCycle;
      if (this.renewalCycle === 'advance_custom') {
        payload.custom_schedules = this.customSchedules.controls.map((control, index) => ({
          renewal_number: index + 1,
          discount_type: control.get('discount_type')?.value as DiscountType,
          discount_value: Number(control.get('discount_value')?.value),
        }));
      }
      // The API rejects the month count on any other cycle, so send it only for Recurring
      // and send null on an edit that switches away, or the old value would stick.
      payload.recurring_renewal_months =
        this.renewalCycle === 'advance_recurring'
          ? this.toFiniteNumber(value.recurring_renewal_months)
          : null;
    }

    return payload;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.submitting) {
      return;
    }

    const error = this.validationError();
    if (error) {
      this.toastService.add({ severity: 'warn', summary: 'Check the form', detail: error });
      return;
    }

    const payload = this.buildPayload();
    this.submitting = true;

    const request$ =
      this.isEditing && this.discount
        ? this.discountService.updateDiscount(this.discount.id, payload)
        : this.discountService.createDiscount(payload);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.submitting = false;
        this.toastService.add({
          severity: 'success',
          summary: this.isEditing ? 'Discount updated' : 'Discount created',
          detail: `${payload.name} is ready to use.`,
        });
        this.saved.emit(response?.data ?? null);
      },
      error: error => {
        this.logger.error('Failed to save discount', error);
        this.submitting = false;
        this.toastService.add({
          severity: 'error',
          summary: 'Could not save',
          detail:
            error?.error?.message || 'Failed to save the discount. Please review and try again.',
        });
      },
    });
  }

  onClear(): void {
    this.packageLoadSeq += 1;
    this.packageOptions = [];
    this.loadingPackages = false;
    this.formReset$.next();
    this.form = this.buildForm();
    this.applyScheduleEnabledState();
    this.submitted = false;
  }

  onCancel(): void {
    DropdownToggleHelper.closeAll();
    this.close.emit();
  }

  // ── Edit prefill (Create never enters here — it uses minStartDate / empty form) ─

  private patchFromDiscount(discount: Discount): void {
    const minimum = Number(discount.minimum_purchase_amount ?? 0);
    const washTypes = discount.wash_types || [];
    const packageIds = discount.package_ids || [];
    // Edit path only: API/DB Start/End → absolute Date → browser-local picker
    // display. Do not replace with browser "now" (Create uses minStartDate for that).
    const startDate = parseWallClockDateTimeInTimeZone(discount.start_date, getUserTimezone());
    const endDate = parseWallClockDateTimeInTimeZone(discount.end_date, getUserTimezone());

    // Apply Location / Wash Type from the API first; Wash Package waits until
    // type-specific options have loaded (see loadPackagesForWashTypes).
    // Enable Start/End before writing dates so the datetime CVA can show both
    // the calendar day and the time slot (matches Campaign Schedule edit load).
    this.form.patchValue({
      name: discount.name || '',
      is_active: discount.is_active !== false,
      location_ids: discount.location_ids || [],
      discount_type: discount.discount_type,
      discount_value: Number(discount.discount_value),
      eligibility: discount.eligibility || 'all_customer',
      always_active: discount.always_active !== false,
      restrict_days_of_week: discount.restrict_days_of_week || [],
      restrict_from_time: normalizeTimeForForm(discount.restrict_from_time),
      restrict_to_time: normalizeTimeForForm(discount.restrict_to_time),
      wash_types: washTypes,
      package_ids: [],
      renewal_cycle: discount.renewal_cycle || 'standard',
      recurring_renewal_months:
        discount.renewal_cycle === 'advance_recurring'
          ? (discount.recurring_renewal_months ?? null)
          : null,
      add_minimum_purchase: minimum > 0,
      minimum_purchase_amount: minimum > 0 ? minimum : null,
    });

    this.applyScheduleEnabledState();

    this.form.patchValue({
      start_date: startDate,
      end_date: endDate,
    });

    this.customSchedules.clear();
    // Saved rows may be stored out of order, and the UI now reads them by position.
    [...(discount.custom_schedules || [])]
      .sort((a, b) => Number(a.renewal_number) - Number(b.renewal_number))
      .forEach(schedule => {
        this.customSchedules.push(this.scheduleGroup(schedule));
      });
    this.renumberSchedules();

    this.loadPackagesForWashTypes(washTypes, { packageIdsToApply: packageIds });
    this.syncConditionalValidators();
  }
}
